import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogMain,
  DialogFooter,
  Button,
} from '@ncoder/ui';
import { Plus, Building2, Shield, FlaskConical, ExternalLink, AlertTriangle } from 'lucide-react';
import type { SalesforceEnvironment } from '@/services/salesforce';
import { useGetAuthorizationUrl } from '@/hooks/auth/useSalesforceAuthGraphQL';
import toast from 'react-hot-toast';

interface AddConnectionDialogProps {
  onAdd?: (name: string, url: string) => void;
}

export const AddConnectionDialog: React.FC<AddConnectionDialogProps> = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedEnv, setSelectedEnv] = useState<'production' | 'sandbox' | 'developer_org' | null>(null);
  
  const getAuthUrlMutation = useGetAuthorizationUrl();

  const handleConnect = () => {
    if (!selectedEnv) {
      toast.error('Please select an environment');
      return;
    }

    // Map developer_org to production for the API call
    const apiEnvironment: SalesforceEnvironment = selectedEnv === 'developer_org' ? 'production' : selectedEnv;

    getAuthUrlMutation.mutate(apiEnvironment, {
      onSuccess: (data) => {
        // Add prompt=login to force Salesforce to ask for credentials every time
        const url = new URL(data.authorizationUrl.url);
        url.searchParams.append('prompt', 'login');
        window.location.href = url.toString();
      },
    });
  };

  const handleCancel = () => {
    setSelectedEnv(null);
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="gap-1.5">
          <Plus className="h-3.5 w-3.5" />
          Add Connection
        </Button>
      </DialogTrigger>
      <DialogContent className="w-full sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add New Connection</DialogTitle>
          <DialogDescription>
            Connect to a new Salesforce organization
          </DialogDescription>
        </DialogHeader>
        
        <DialogMain>
          <div className="space-y-3">
            <label className="text-sm font-medium">
              Select Environment Type
            </label>
            
            <div className="space-y-2">
              {/* Production */}
              <button
                onClick={() => setSelectedEnv('production')}
                disabled={getAuthUrlMutation.isPending}
                className={`w-full flex items-start gap-3 px-4 py-3 rounded-md border text-left transition-colors ${
                  selectedEnv === 'production'
                    ? 'border-primary bg-primary/10'
                    : 'border-border hover:bg-accent'
                } ${getAuthUrlMutation.isPending ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <Building2 className="h-5 w-5 mt-0.5" />
                <div className="flex-1">
                  <div className="font-medium">Production</div>
                  <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                    <FlaskConical className="h-3 w-3" /> Supports Dev Hub & scratch org creation
                  </div>
                </div>
              </button>

              {/* Developer Org */}
              <button
                onClick={() => setSelectedEnv('developer_org')}
                disabled={getAuthUrlMutation.isPending}
                className={`w-full flex items-start gap-3 px-4 py-3 rounded-md border text-left transition-colors ${
                  selectedEnv === 'developer_org'
                    ? 'border-primary bg-primary/10'
                    : 'border-border hover:bg-accent'
                } ${getAuthUrlMutation.isPending ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <Building2 className="h-5 w-5 mt-0.5" />
                <div className="flex-1">
                  <div className="font-medium">Developer Org</div>
                  <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                    <FlaskConical className="h-3 w-3" /> Supports Dev Hub & scratch org creation
                  </div>
                </div>
              </button>

              {/* Sandbox — explicitly warned */}
              <button
                onClick={() => setSelectedEnv('sandbox')}
                disabled={getAuthUrlMutation.isPending}
                className={`w-full flex items-start gap-3 px-4 py-3 rounded-md border text-left transition-colors ${
                  selectedEnv === 'sandbox'
                    ? 'border-primary bg-primary/10'
                    : 'border-border hover:bg-accent'
                } ${getAuthUrlMutation.isPending ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <Shield className="h-5 w-5 mt-0.5" />
                <div className="flex-1">
                  <div className="font-medium">Sandbox</div>
                  <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3 text-warning" />
                    Sandboxes <strong>cannot</strong> host Dev Hub — pick Production or Developer Org for scratch orgs
                  </div>
                </div>
              </button>
            </div>

            {selectedEnv === 'sandbox' && (
              <div className="mt-3 rounded-md border border-warning/40 bg-warning/10 p-3 text-xs text-foreground/85 flex items-start gap-2">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-warning" />
                <div>
                  Heads up — sandboxes work for chat, code analysis, and most ncoder features, but they
                  <strong> cannot create scratch orgs</strong>. For the Scratch Org / Testing tabs to work, you'll
                  also need a Production or Developer Org connection.{' '}
                  <a
                    href="https://developer.salesforce.com/signup"
                    target="_blank"
                    rel="noreferrer noopener"
                    className="inline-flex items-center gap-1 underline hover:no-underline"
                  >
                    Sign up for a free Developer Org <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              </div>
            )}
          </div>
        </DialogMain>

        <DialogFooter>
          <Button 
            onClick={handleCancel} 
            disabled={getAuthUrlMutation.isPending}
            className="border-2 border-primary bg-background text-primary hover:bg-primary hover:text-primary-foreground"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConnect} 
            disabled={!selectedEnv || getAuthUrlMutation.isPending}
          >
            {getAuthUrlMutation.isPending ? 'Connecting...' : 'Connect'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
